import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useStarterPlan } from "../../contexts/OrganizationPlanContext";
import { ArrowLeft, X, Download, Eye, Send, ChevronDown } from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";

const BugDetails = ({ darkMode, bugData }) => {
  const navigate = useNavigate();
  const { bugId } = useParams();
  const { currentUser, selectedProject } = useSelector((state) => state.auth);
  const starterBypass = useStarterPlan();
  const projectRole = selectedProject?.user_role;
  const canEditBugs =
    starterBypass || (currentUser?.role !== "org_admin" && projectRole === "qa");
  const canUpdateBugStatus =
    starterBypass ||
    (currentUser?.role !== "org_admin" &&
    (projectRole === "qa" || projectRole === "developer"));
  const canCommentOnBugs = canUpdateBugStatus;
  const [bug, setBug] = useState(bugData || null);
  const [previewImage, setPreviewImage] = useState(null);
  const [loadingBug, setLoadingBug] = useState(false);

  // COMMENTS STATE
  const [commentText, setCommentText] = useState("");
  const [loadingComment, setLoadingComment] = useState(false);

  const safe = (val) => {
    if (!val) return "N/A";
    if (typeof val === "object") return JSON.stringify(val);
    return val;
  };

  const assigneeLabel = (val) => {
    if (Array.isArray(bug?.assigned_to_names) && bug.assigned_to_names.length) {
      return bug.assigned_to_names.join(", ");
    }
    if (typeof bug?.assigned_to_label === "string" && bug.assigned_to_label.trim()) {
      return bug.assigned_to_label;
    }
    if (Array.isArray(val)) {
      return val.length ? val.join(", ") : "Unassigned";
    }
    if (typeof val === "string" && val.trim()) {
      return val;
    }
    if (typeof val === "number") {
      return String(val);
    }
    return "Unassigned";
  };

  const bgMain = darkMode ? "bg-[#0b0d13]" : "bg-gray-50";
  const cardBg = darkMode ? "bg-[#13161f]" : "bg-white";
  const border = darkMode ? "border-gray-800" : "border-gray-200";
  const textMain = darkMode ? "text-white" : "text-gray-900";
  const textMuted = darkMode ? "text-gray-400" : "text-gray-500";

  useEffect(() => {
    const loadBug = async () => {
      if (!bugId) return;
      setLoadingBug(true);

      try {
        const res = await axios.get(
          `https://api.testoraglobal.in/api/bug/id/${bugId}`,
        );
        setBug(res.data);
      } catch (error) {
        console.error("Failed to load bug", error);
      } finally {
        setLoadingBug(false);
      }
    };

    if (!bugData) {
      loadBug();
    }
  }, [bugData, bugId]);

  // Handle Dropdown Status Change
  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;

    // 1. Safety check
    if (!bug || !bug.bug_id) {
      toast.error("Bug ID is missing.");
      return;
    }

    // 2. Prevent redundant API calls
    if (newStatus === bug.status?.toLowerCase()) return;

    try {
      // 3. Perform the PUT request.
      const response = await axios.put(
        `https://api.testoraglobal.in/api/bug/update-status/${bug.bug_id}`,
        { status: newStatus },
      );

      if (response.data.success) {
        // 4. Update local state
        setBug((prev) => ({ ...prev, status: newStatus }));
        toast.success(`Bug Status Updated to ${newStatus}`);
      } else {
        throw new Error(response.data.message || "Update failed");
      }
    } catch (error) {
      console.error("Status update error:", error);
      toast.error(error.response?.data?.message || "Failed to update status");

      // 5. Revert the UI if the update failed
      e.target.value = bug.status;
    }
  };

  // Helper to determine dropdown options based on current status
  const getStatusOptions = (currentStatus) => {
    const statusStr = (currentStatus || "").toLowerCase();

    if (statusStr === "open") return ["open", "inprogress", "resolve"];
    if (statusStr === "inprogress") return ["inprogress", "reopen", "resolve"];
    if (statusStr === "resolve" || statusStr === "resolved")
      return ["resolve", "reopen"];
    if (statusStr === "reopen") return ["reopen", "inprogress", "resolve"];

    return Array.from(
      new Set([statusStr, "open", "inprogress", "reopen", "resolve", "retest"]),
    );
  };

  // ADD COMMENT FUNCTION
  const handleAddComment = async () => {
    if (!commentText.trim()) {
      toast.error("Comment cannot be empty");
      return;
    }

    try {
      setLoadingComment(true);

      const res = await axios.post(
        `https://api.testoraglobal.in/api/bug/add-comment/${bug.bug_id}`,
        {
          comment: commentText,
          user: "You",
        },
      );

      setBug((prev) => ({
        ...prev,
        comments: res.data.comments,
      }));

      setCommentText("");
      toast.success("Comment added");
    } catch {
      toast.error("Failed to add comment");
    } finally {
      setLoadingComment(false);
    }
  };

  if (loadingBug || !bug) {
    return (
      <div className="px-8 pb-12 max-w-7xl mx-auto animate-in duration-300 text-center text-gray-400">
        {loadingBug ? "Loading bug details..." : "Bug not found."}
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full ${bgMain}`}>
      {/* HEADER */}
      <div className={`p-6 border-b ${border} flex justify-between`}>
        <div className="flex gap-4 items-center">
          <button
            onClick={() => navigate("/bug-optimizer")}
            className={textMuted}
          >
            <ArrowLeft />
          </button>

          <div>
            <div className="flex gap-2 mb-1">
              <span className="text-xs text-purple-400">
                {safe(bug.bug_id)}
              </span>
              <span className="text-xs text-red-400">{safe(bug.priority)}</span>
              <span className="text-xs text-blue-400">{safe(bug.status)}</span>
            </div>
            <h2 className={`text-xl font-bold ${textMain}`}>
              {safe(bug.title)}
            </h2>
          </div>
        </div>

        <div className="flex gap-3">
          {/* UPDATED EDIT BUTTON */}
          {canEditBugs && (
            <button
              onClick={() => navigate(`/edit-bug/${bug.bug_id}`)}
              className="px-4 py-2 rounded-lg border border-purple-500 text-purple-400 hover:bg-purple-500 hover:text-white transition cursor-pointer"
            >
              Edit
            </button>
          )}

          {canUpdateBugStatus ? (
            <div className="relative flex items-center">
              <select
                value={bug.status?.toLowerCase() || ""}
                onChange={handleStatusChange}
                className="appearance-none bg-none pl-4 pr-10 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white shadow-lg outline-none cursor-pointer capitalize transition font-medium"
              >
                {getStatusOptions(bug.status).map((opt) => (
                  <option
                    key={opt}
                    value={opt}
                    className={`capitalize ${darkMode ? "bg-gray-800 text-white" : "bg-white text-black"}`}
                  >
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <span className="px-4 py-2 rounded-lg border border-purple-500/30 text-purple-400 capitalize">
              {safe(bug.status)}
            </span>
          )}
        </div>
      </div>

      {/* BODY */}
      <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT */}
        <div className="lg:col-span-2 space-y-6">
          {/* DESCRIPTION */}
          <div className={`p-6 rounded-xl border ${border} ${cardBg} shadow`}>
            <h3 className={textMuted}>Description</h3>
            <p className={textMain}>{safe(bug.description)}</p>
          </div>

          {/* STEPS */}
          <div className={`p-6 rounded-xl border ${border} ${cardBg} shadow`}>
            <h3 className={`${textMuted} mb-5`}>Steps to Reproduce</h3>

            {bug.steps && bug.steps.length > 0 ? (
              <div className="space-y-4">
                {bug.steps.map((step, index) => {
                  const stepText =
                    typeof step === "string"
                      ? step
                      : step?.action || "No step info";

                  return (
                    <div
                      key={index}
                      className={`flex items-start gap-4 p-4 rounded-xl border ${border} hover:shadow-md transition`}
                    >
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-purple-600 text-white font-bold shadow shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1 pt-1.5">
                        <p className={`text-sm ${textMain} leading-relaxed`}>
                          {stepText}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className={textMuted}>No steps provided</p>
            )}
          </div>

          {/* EXPECTED / ACTUAL */}
          <div className="grid grid-cols-2 gap-4">
            <div className={`p-4 border ${border} rounded-xl ${cardBg} shadow`}>
              <h4 className={textMuted}>Expected Result</h4>
              <p className={textMain}>{safe(bug.expected_result)}</p>
            </div>

            <div
              className={`p-4 border border-red-500/30 rounded-xl ${cardBg} shadow`}
            >
              <h4 className="text-red-400">Actual Result</h4>
              <p className={textMain}>{safe(bug.actual_result)}</p>
            </div>
          </div>

          {/* IMAGE CARD UI */}
          <div className={`p-6 rounded-xl border ${border} ${cardBg} shadow`}>
            <h3 className={textMuted}>Screenshots</h3>

            {bug.images && bug.images.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mt-4">
                {bug.images.map((img, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.04 }}
                    className="relative rounded-xl overflow-hidden group border"
                  >
                    <img
                      src={img}
                      alt="bug"
                      className="w-full h-40 object-cover"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-start justify-end p-2 gap-2">
                      <button
                        onClick={() => setPreviewImage(img)}
                        className="bg-white/90 hover:bg-white text-black p-2 rounded-full shadow cursor-pointer"
                      >
                        <Eye size={14} />
                      </button>
                      <a
                        href={img}
                        download
                        target="_blank"
                        rel="noreferrer"
                        className="bg-white/90 hover:bg-white text-black p-2 rounded-full shadow cursor-pointer"
                      >
                        <Download size={14} />
                      </a>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <p className={textMuted}>No images uploaded</p>
            )}
          </div>
        </div>

        {/* RIGHT */}
        {/* WRAPPED IN FLEX CONTAINER TO MATCH LEFT SIDE HEIGHT */}
        <div className="lg:col-span-1 flex flex-col gap-6 h-full">
          {/* DETAILS CARD - SHRINKS TO FIT */}
          <div
            className={`p-6 border ${border} rounded-xl ${cardBg} shadow shrink-0`}
          >
            <h3 className={textMuted}>Details</h3>
            <div className="space-y-2 mt-3 text-sm">
              <p>
                <b>Assignee:</b> {assigneeLabel(bug.assigned_to || bug.assigned_to_names || bug.assigned_to_label)}
              </p>
              <p>
                <b>Module:</b> {safe(bug.module)}
              </p>
              <p>
                <b>Severity:</b> {safe(bug.severity)}
              </p>
              <p>
                <b>Status:</b> {safe(bug.status)}
              </p>
            </div>
          </div>

          {/* COMMENTS CARD */}
          <div
            className={`p-6 border ${border} rounded-xl ${cardBg} shadow h-fit`}
          >
            <h3 className={`${textMuted} mb-4`}>Comments</h3>

            <div
              className="overflow-y-auto pr-2 custom-scrollbar"
              style={{
                maxHeight: "320px",
                display: "flex",
                flexDirection: "column-reverse",
              }}
            >
              <div className="space-y-3 w-full">
                {bug.comments && bug.comments.length > 0 ? (
                  bug.comments.map((c, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className={`p-3 rounded-lg border ${border} bg-white/5 hover:bg-white/10 transition-colors`}
                    >
                      <p className={`text-sm ${textMain} line-clamp-3`}>
                        {c.comment || "No message"}
                      </p>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-[10px] font-medium text-purple-400 uppercase tracking-wider">
                          {c.user || "User"}
                        </span>
                        <span className="text-[10px] text-gray-500">
                          {c.date || "Just now"}
                        </span>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <p className={`${textMuted} text-center py-10`}>
                    No comments yet
                  </p>
                )}
              </div>
            </div>

            {canCommentOnBugs && (
              <div className="mt-5 pt-4 border-t border-white/5">
                <div
                  className={`
                    relative flex items-end gap-2 p-1.5 rounded-2xl
                    border ${border} bg-black/30 focus-within:ring-1
                    focus-within:ring-purple-500/50 transition-all duration-200
                  `}
                >
                  <textarea
                    rows="1"
                    value={commentText}
                    onChange={(e) => {
                      setCommentText(e.target.value);
                      e.target.style.height = "inherit";
                      e.target.style.height = `${e.target.scrollHeight}px`;
                    }}
                    placeholder="Ask a question or leave a comment..."
                    className={`
                      flex-1 bg-transparent border-none outline-none
                      py-2 px-3 text-sm ${textMain} placeholder-gray-500
                      resize-none max-h-32 min-h-10
                    `}
                  />

                  <button
                    onClick={handleAddComment}
                    disabled={loadingComment || !commentText.trim()}
                    className={`
                      flex items-center justify-center
                      h-9 w-9 rounded-xl transition-all duration-200 shrink-0
                      ${
                        commentText.trim()
                          ? "bg-purple-600 text-white shadow-lg shadow-purple-500/20 hover:bg-purple-500"
                          : "bg-white/5 text-gray-500 cursor-not-allowed"
                      }
                    `}
                  >
                    {loadingComment ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Send
                        size={16}
                        className={
                          commentText.trim()
                            ? "translate-x-0.5 -translate-y-0.5"
                            : ""
                        }
                      />
                    )}
                  </button>
                </div>
                <p className="text-[10px] text-gray-500 mt-2 px-2 italic">
                  Press enter to post, Shift+Enter for new line
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* IMAGE MODAL */}
      <AnimatePresence>
        {previewImage && (
          <motion.div
            className="fixed inset-0 bg-black/90 flex justify-center items-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="relative w-96 max-h-[50vh] bg-[#13161f] p-2 rounded-xl border border-gray-700 shadow-2xl">
              <img
                src={previewImage}
                alt="preview"
                className="w-full h-auto max-h-[45vh] object-contain rounded-lg"
              />

              <button
                onClick={() => setPreviewImage(null)}
                className="absolute -top-3 -right-3 bg-red-500 text-white p-2 rounded-full shadow-lg"
              >
                <X size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BugDetails;